// Pure "place × time" swimlane transform. Lanes are places; bars are people's stays.
// No I/O, no Date.now — the window is passed in. Feeds the <Lanes> UI across the
// Calendar / Group / Circle surfaces. Privacy is upstream: a country-only (redacted)
// item simply has city=null and is omitted from city lanes.

export type Granularity = "country" | "city";

export interface LanesWindow {
  start: string; // YYYY-MM-DD inclusive
  end: string; // YYYY-MM-DD inclusive
}

export interface LaneItem {
  id: string;
  person_id: string;
  person_name: string;
  city: string | null;
  country: string | null;
  country_code: string | null;
  date_from: string;
  date_to: string | null; // null = ongoing
  type?: string;
}

export interface LaneBar {
  id: string;
  person_id: string;
  person_name: string;
  place: string;
  startFrac: number; // 0..1 within the window
  endFrac: number; // 0..1
  continuesLeft: boolean;
  continuesRight: boolean;
}

export interface LaneOverlap {
  startFrac: number;
  endFrac: number;
  people: string[]; // display names
  count: number;
}

export interface Lane {
  key: string;
  label: string;
  countryCode: string | null;
  peopleCount: number;
  bars: LaneBar[];
  overlaps: LaneOverlap[];
}

const DAY = 86_400_000;

function dayMs(d: string): number {
  return Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
}

const iso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export type Zoom = "week" | "month" | "quarter" | "year";

const ZOOM_SPANS: Record<Exclude<Zoom, "year">, { days: number; lead: number }> = {
  week: { days: 10, lead: 2 },
  month: { days: 31, lead: 3 },
  quarter: { days: 91, lead: 10 },
};

/** The visible window for a zoom level, anchored near `anchor` (YYYY-MM-DD). Year
 *  snaps to the calendar year; others lead a little before the anchor. */
export function lanesWindow(zoom: Zoom, anchor: string): LanesWindow {
  if (zoom === "year") {
    const y = anchor.slice(0, 4);
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
  const cfg = ZOOM_SPANS[zoom];
  const start = dayMs(anchor) - cfg.lead * DAY;
  return { start: iso(start), end: iso(start + (cfg.days - 1) * DAY) };
}

/** Pan the anchor by one window in `dir` (-1 back, +1 forward). */
export function panAnchor(zoom: Zoom, anchor: string, dir: -1 | 1): string {
  if (zoom === "year") return `${Number(anchor.slice(0, 4)) + dir}-${anchor.slice(5)}`;
  return iso(dayMs(anchor) + dir * ZOOM_SPANS[zoom].days * DAY);
}

function overlapsOf(bars: LaneBar[]): LaneOverlap[] {
  if (bars.length < 2) return [];
  const points = [...new Set(bars.flatMap((b) => [b.startFrac, b.endFrac]))].sort((a, b) => a - b);
  const raw: LaneOverlap[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i] as number;
    const b = points[i + 1] as number;
    const mid = (a + b) / 2;
    const active = bars.filter((bar) => bar.startFrac <= mid && mid < bar.endFrac);
    const byPerson = new Map<string, string>();
    for (const bar of active) byPerson.set(bar.person_id, bar.person_name);
    if (byPerson.size >= 2) {
      raw.push({ startFrac: a, endFrac: b, people: [...byPerson.values()], count: byPerson.size });
    }
  }
  // merge touching segments, union the people
  const merged: LaneOverlap[] = [];
  for (const seg of raw) {
    const prev = merged[merged.length - 1];
    if (prev && Math.abs(prev.endFrac - seg.startFrac) < 1e-6) {
      prev.endFrac = seg.endFrac;
      const names = new Set([...prev.people, ...seg.people]);
      prev.people = [...names];
      prev.count = names.size;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

export function buildLanes(
  items: LaneItem[],
  opts: { granularity: Granularity; window: LanesWindow },
): Lane[] {
  const winStart = dayMs(opts.window.start);
  const winEndExcl = dayMs(opts.window.end) + DAY;
  const span = winEndExcl - winStart;
  if (span <= 0) return [];

  const lanes = new Map<
    string,
    { label: string; countryCode: string | null; bars: LaneBar[]; people: Set<string> }
  >();

  for (const it of items) {
    let key: string;
    let label: string;
    if (opts.granularity === "city") {
      if (!it.city) continue; // country-only/redacted: no city lane
      key = `${it.country_code ?? it.country ?? ""}|${it.city}`;
      label = it.city;
    } else {
      const ck = it.country_code ?? it.country;
      if (!ck) continue;
      key = ck;
      label = it.country ?? ck;
    }

    const barStart = dayMs(it.date_from);
    const endDayMs = it.date_to ? dayMs(it.date_to) : dayMs(opts.window.end);
    const barEndExcl = endDayMs + DAY;
    if (barEndExcl <= winStart || barStart >= winEndExcl) continue; // outside the window

    const clippedStart = Math.max(barStart, winStart);
    const clippedEndExcl = Math.min(barEndExcl, winEndExcl);
    const bar: LaneBar = {
      id: it.id,
      person_id: it.person_id,
      person_name: it.person_name,
      place: it.city ?? it.country ?? "",
      startFrac: (clippedStart - winStart) / span,
      endFrac: (clippedEndExcl - winStart) / span,
      continuesLeft: barStart < winStart,
      continuesRight: it.date_to == null || barEndExcl > winEndExcl,
    };

    let lane = lanes.get(key);
    if (!lane) {
      lane = { label, countryCode: it.country_code ?? null, bars: [], people: new Set() };
      lanes.set(key, lane);
    }
    lane.bars.push(bar);
    lane.people.add(it.person_id);
  }

  return [...lanes.entries()]
    .map(([key, l]) => ({
      key,
      label: l.label,
      countryCode: l.countryCode,
      peopleCount: l.people.size,
      bars: l.bars,
      overlaps: overlapsOf(l.bars),
    }))
    .sort((a, b) => b.peopleCount - a.peopleCount || a.label.localeCompare(b.label));
}
